import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  AlertTitle,
  CircularProgress,
  IconButton,
  Divider,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Refresh as RefreshIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import MainLayout from '../components/MainLayout';
import {
  getSubscription,
  createCheckoutSession,
  cancelSubscription,
  updateSubscriptionSeats,
  syncWithStripe,
  getBillingHistory,
  verifyCheckoutSession,
  getPendingChanges,
  cancelPendingReduction,
  createBillingPortalSession,
} from '../services/billingService';
import { SubscriptionData, Invoice } from '../types/subscription';
import { auth } from '../config/firebase';
import { apiService } from '../services/apiService';
import moment from 'moment';
import { DATE_FORMAT } from '../constants/dateFormats';

const Subscription: React.FC = () => {

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Add component lifecycle logging
  React.useEffect(() => {
    console.log('[Subscription Component] ✅ MOUNTED');
    return () => {
      console.log('[Subscription Component] ❌ UNMOUNTING');
    };
  }, []);

  // Dialog states
  const [cancelDialog, setCancelDialog] = useState(false);
  const [updateSeatsDialog, setUpdateSeatsDialog] = useState(false);
  const [updateSeatsConfirmDialog, setUpdateSeatsConfirmDialog] = useState(false);
  const [createSubDialog, setCreateSubDialog] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);
  const [checkoutDetails, setCheckoutDetails] = useState<{
    amountPaid: number;
    totalSeats: number;
    additionalSeats: number;
  } | null>(null);

  // Form states
  const [additionalUsers, setAdditionalUsers] = useState(0);
  const [cancellationReason, setCancellationReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // User selection for seat reduction
  const [userSelectionDialog, setUserSelectionDialog] = useState(false);
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const [selectedUsersToDeactivate, setSelectedUsersToDeactivate] = useState<string[]>([]);
  const [requiredDeactivations, setRequiredDeactivations] = useState(0);

  // Pending changes state
  const [pendingChanges, setPendingChanges] = useState<any>(null);

  // Payment method portal state
  const [updatingPaymentMethod, setUpdatingPaymentMethod] = useState(false);
  // Tracks whether the Stripe portal tab was opened so we can refresh on return
  const portalOpenedRef = React.useRef(false);

  // Flag to prevent re-running useEffect while handling checkout
  const isHandlingCheckoutRef = React.useRef(false);
  const hasHandledCheckoutRef = React.useRef(false);

  useEffect(() => {
    console.log('[Subscription useEffect] Running with searchParams:', {
      sessionId: searchParams.get('session_id'),
      hasHandledCheckout: hasHandledCheckoutRef.current,
      isHandlingCheckout: isHandlingCheckoutRef.current,
      currentPath: window.location.pathname,
      fullUrl: window.location.href
    });

    // Check for checkout session completion FIRST before loading data
    const sessionId = searchParams.get('session_id');
    if (sessionId && !hasHandledCheckoutRef.current) {
      console.log('[Subscription useEffect] ✅ Session ID detected, starting checkout handling');
      // Mark that we're handling checkout to prevent re-entry
      hasHandledCheckoutRef.current = true;
      // Handle checkout success and it will load data internally
      handleCheckoutSuccess(sessionId);
    } else if (!sessionId && !isHandlingCheckoutRef.current) {
      console.log('[Subscription useEffect] Loading normal subscription data (no session_id)');
      // Only load subscription data if not handling checkout
      loadSubscriptionData();
    } else {
      console.log('[Subscription useEffect] ⚠️ Skipping - already handling or handled checkout');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      const subData = await getSubscription();
      setSubscription(subData);

      // Load billing history
      const invoiceData = await getBillingHistory(20);
      setInvoices(invoiceData);

      // Load pending changes
      try {
        const pending = await getPendingChanges();
        setPendingChanges(pending);
      } catch (pendingError) {
        console.error('Error loading pending changes:', pendingError);
        // Don't fail the whole load if pending changes fail
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load subscription data');
      console.error('Error loading subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckoutSuccess = async (sessionId: string) => {
    console.log('[handleCheckoutSuccess] 🚀 START - sessionId:', sessionId);
    console.log('[handleCheckoutSuccess] Current location:', window.location.href);

    // Set flag to indicate we're handling checkout
    isHandlingCheckoutRef.current = true;

    try {
      setProcessing(true);
      setLoading(true);
      setSuccess('Processing your subscription...');

      // Verify checkout session and sync with Stripe
      console.log('[handleCheckoutSuccess] Step 1: Verifying checkout session...');
      await verifyCheckoutSession(sessionId);
      console.log('[handleCheckoutSuccess] Step 1: ✅ Checkout session verified');

      // Force sync with Stripe to get latest subscription data
      console.log('[handleCheckoutSuccess] Step 2: Syncing with Stripe...');
      await syncWithStripe();
      console.log('[handleCheckoutSuccess] Step 2: ✅ Synced with Stripe');

      // Wait a bit for webhooks to process
      console.log('[handleCheckoutSuccess] Step 3: Waiting 2s for webhooks...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('[handleCheckoutSuccess] Step 3: ✅ Wait complete');

      // Reload subscription data to get the latest info
      console.log('[handleCheckoutSuccess] Step 4: Getting subscription data...');
      const subData = await getSubscription();
      console.log('[handleCheckoutSuccess] Step 4: ✅ Subscription data received:', subData);
      setSubscription(subData);

      // Prepare checkout details for success dialog
      const details = {
        amountPaid: subData.subscription.totalAmount || 0,
        totalSeats: subData.subscription.totalUserSeats || 1,
        additionalSeats: subData.subscription.additionalUserSeats || 0,
      };
      console.log('[handleCheckoutSuccess] Step 5: Setting checkout details:', details);
      setCheckoutDetails(details);

      // Show success dialog
      console.log('[handleCheckoutSuccess] Step 6: Showing success dialog');
      setSuccessDialog(true);
      setSuccess(null);
      setError(null);

      // Reload full subscription data including billing history and pending changes
      console.log('[handleCheckoutSuccess] Step 7: Reloading full subscription data...');
      await loadSubscriptionData();
      console.log('[handleCheckoutSuccess] Step 7: ✅ Full subscription data reloaded');

      // Remove session_id from URL AFTER everything is done
      console.log('[handleCheckoutSuccess] Step 8: Cleaning URL...');
      window.history.replaceState({}, '', window.location.pathname);
      console.log('[handleCheckoutSuccess] Step 8: ✅ URL cleaned');

      console.log('[handleCheckoutSuccess] ✅ COMPLETE - Success dialog should be visible');
    } catch (err: any) {
      console.error('[handleCheckoutSuccess] ❌ ERROR:', err);
      console.error('[handleCheckoutSuccess] Error details:', {
        message: err.message,
        error: err.error,
        stack: err.stack
      });
      setError(err.message || 'Failed to verify checkout session. Your subscription may have been created - please check your subscription status.');
      // Still remove the session_id from URL even on error
      window.history.replaceState({}, '', window.location.pathname);
    } finally {
      console.log('[handleCheckoutSuccess] 🏁 FINALLY block');
      setProcessing(false);
      setLoading(false);
      isHandlingCheckoutRef.current = false;
    }
  };

  const handleCreateSubscription = async () => {
    try {
      setProcessing(true);
      setError(null);

      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('User not authenticated');
      }

      const { checkoutUrl } = await createCheckoutSession(user.email, additionalUsers);

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setError(err.message || 'Failed to create checkout session');
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setProcessing(true);
      setError(null);

      await cancelSubscription(cancellationReason);
      setSuccess('Subscription will be cancelled at the end of the billing period');

      setCancelDialog(false);
      await loadSubscriptionData();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleUserSelectionToggle = (userId: string) => {
    setSelectedUsersToDeactivate(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleProceedWithUserSelection = () => {
    if (selectedUsersToDeactivate.length !== requiredDeactivations) {
      setError(`Please select exactly ${requiredDeactivations} user(s) to deactivate`);
      return;
    }
    setUserSelectionDialog(false);
    setUpdateSeatsConfirmDialog(true);
  };

  const handleUpdateSeatsConfirm = async () => {
    // Check if we're reducing seats below the current used seats
    if (subscription && additionalUsers < (subscription.subscription.additionalUserSeats || 0)) {
      const newTotalSeats = 1 + additionalUsers; // 1 base seat + additional
      const currentUsedSeats = subscription.subscription.usedUserSeats || 0;

      if (newTotalSeats < currentUsedSeats) {
        // Need to deactivate users
        const usersToDeactivate = currentUsedSeats - newTotalSeats;
        setRequiredDeactivations(usersToDeactivate);

        // Fetch active users
        try {
          const response = await apiService.get('/admin/users', {
            isActive: 'true',
            limit: '100'
          });

          if (response.success && response.data) {
            setActiveUsers(response.data.users || []);
            setSelectedUsersToDeactivate([]);
            setUserSelectionDialog(true);
          } else {
            setError('Failed to fetch active users');
          }
        } catch (err) {
          setError('Failed to fetch active users');
        }
        return;
      }
    }

    // Show confirmation dialog if no user deactivation needed
    setUpdateSeatsConfirmDialog(true);
  };

  const handleUpdateSeats = async () => {
    try {
      setProcessing(true);
      setError(null);

      // Prepare usersToDeactivate with full details for seat reductions
      const usersToDeactivate = selectedUsersToDeactivate.map(userId => {
        const user = activeUsers.find(u => (u._id || u.id) === userId);
        if (!user) {
          console.error('User not found for userId:', userId);
          throw new Error(`User not found: ${userId}`);
        }
        return {
          userId: user._id || user.id,
          email: user.email,
          firebaseUid: user.firebaseUid
        };
      });

      const response = await updateSubscriptionSeats(additionalUsers, usersToDeactivate);

      // Handle different response types
      const seatChange = response.data?.seatChange;

      if (seatChange?.isPending) {
        // Seat reduction scheduled for period end
        const scheduledDate = moment(seatChange.scheduledFor * 1000).format(DATE_FORMAT);
        setSuccess(`Seat reduction scheduled for ${scheduledDate}. Users will remain active until then.`);
      } else if (response.data?.invoice) {
        // Immediate charge/credit for upgrades
        const invoice = response.data.invoice;
        const isUpgrade = seatChange?.isUpgrade;

        if (isUpgrade && invoice.status === 'paid') {
          setSuccess(`Seats updated successfully! Payment of ${formatAmount(invoice.amountPaid, invoice.currency)} has been processed.`);
        } else if (isUpgrade && invoice.status === 'open') {
          setError(`Seats updated but payment is pending. Please check your payment method.`);
        } else {
          setSuccess('User seats updated successfully!');
        }

        console.log('Invoice created:', invoice);
      } else {
        setSuccess(response.message || 'User seats updated successfully!');
      }

      setUpdateSeatsDialog(false);
      setUpdateSeatsConfirmDialog(false);
      setUserSelectionDialog(false);
      setSelectedUsersToDeactivate([]);

      // Reload subscription data to get updated billing history and pending changes
      await loadSubscriptionData();
    } catch (err: any) {
      setError(err.message || 'Failed to update seats');
      // Don't close dialogs on error so user can retry
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      setUpdatingPaymentMethod(true);
      setError(null);
      const { url } = await createBillingPortalSession();
      portalOpenedRef.current = true;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err.message || 'Failed to open payment method management. Please try again.');
    } finally {
      setUpdatingPaymentMethod(false);
    }
  };

  // Refresh subscription data when the user returns to this tab after the Stripe portal
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && portalOpenedRef.current) {
        portalOpenedRef.current = false;
        loadSubscriptionData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  // loadSubscriptionData is stable (defined inside component but doesn't change reference)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSyncWithStripe = async () => {
    try {
      setProcessing(true);
      setError(null);

      await syncWithStripe();
      setSuccess('Synced with Stripe successfully!');

      await loadSubscriptionData();
    } catch (err: any) {
      setError(err.message || 'Failed to sync with Stripe');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'past_due':
      case 'grace_period':
        return 'warning';
      case 'canceled':
      case 'unpaid':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon />;
      case 'past_due':
      case 'grace_period':
        return <WarningIcon />;
      case 'canceled':
      case 'unpaid':
        return <ErrorIcon />;
      default:
        return undefined;
    }
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'N/A';
    return moment(timestamp * 1000).format(DATE_FORMAT);
  };

  const formatAmount = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getDaysRemaining = (endDate: number | null) => {
    if (!endDate) return 0;
    const now = Math.floor(Date.now() / 1000);
    const diff = endDate - now;
    return Math.max(0, Math.ceil(diff / (24 * 60 * 60)));
  };

  if (loading) {
    return (
      <MainLayout title="Subscription & Billing">
        <Box className="flex justify-center items-center h-96">
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Subscription & Billing">
      <Box className="p-2">
        <div className="mb-3 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Subscription & Billing</h1>
            <p className="text-gray-600 mt-1">Manage your subscription and billing settings</p>
          </div>
          {!subscription?.trialAccess?.enabled && (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleSyncWithStripe}
              disabled={processing}
            >
              Sync with Stripe
            </Button>
          )}
        </div>

        {/* Alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} className="mb-4">
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} className="mb-4">
            {success}
          </Alert>
        )}

        {/* Grace Period Warning */}
        {!subscription?.trialAccess?.enabled && subscription?.subscription.status === 'grace_period' && (
          <Alert severity="warning" className="mb-4">
            <Typography variant="body1" className="font-semibold">
              Your subscription is in grace period
            </Typography>
            <Typography variant="body2">
              {getDaysRemaining(subscription.subscription.gracePeriodEndDate)} days remaining.
              Please update your payment method to avoid service interruption.
            </Typography>
          </Alert>
        )}

        {subscription?.trialAccess?.enabled ? (
          <Card>
            <CardContent>
              <Box className="flex justify-between items-center mb-4">
                <Box>
                  <Typography variant="h6" className="font-semibold">
                    Live Trial
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {subscription?.org_name || subscription?.org_id} has unrestricted access.
                  </Typography>
                </Box>
                <Chip
                  label="TRIAL ACTIVE"
                  color="success"
                  icon={<CheckCircleIcon />}
                />
              </Box>

              <Divider className="my-3" />

              <Grid container spacing={3} className="mt-2">
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    Status
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    Active (no expiration)
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    Enabled On
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {subscription?.trialAccess?.enabledAt
                      ? moment.unix(subscription.trialAccess.enabledAt).format(DATE_FORMAT)
                      : '—'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="body2" color="textSecondary">
                    User Seats
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    Unlimited
                  </Typography>
                </Grid>
                {subscription?.trialAccess?.reason && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="textSecondary">
                      Reason
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {subscription.trialAccess.reason}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Alert severity="info" className="mt-4">
                Authorized users are still enforced. Add or remove users from the Users page as usual.
                To switch to a paid plan, disable the trial flag on the organization record.
              </Alert>
            </CardContent>
          </Card>
        ) : (
        <Grid container spacing={3}>
          {/* Subscription Overview */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Box className="flex justify-between items-center mb-4">
                  <Typography variant="h6" className="font-semibold">
                    Subscription Overview
                  </Typography>
                  <Chip
                    label={subscription?.subscription.status.replace('_', ' ').toUpperCase()}
                    color={getStatusColor(subscription?.subscription.status || 'none')}
                    icon={getStatusIcon(subscription?.subscription.status || 'none')}
                  />
                </Box>

                {subscription?.subscription.status === 'none' || subscription?.subscription.status === 'canceled' ? (
                  <Box>
                    {subscription?.subscription.status === 'canceled' && (
                      <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Previous Subscription History
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <Typography variant="body2" color="textSecondary">
                              Start Date
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {formatDate(subscription?.subscription.currentPeriodStart || null)}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <Typography variant="body2" color="textSecondary">
                              End Date
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {formatDate(subscription?.subscription.canceledAt || null)}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <Typography variant="body2" color="textSecondary">
                              Total User Seats
                            </Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {subscription?.subscription.totalUserSeats || 0} seats
                              {subscription?.subscription.additionalUserSeats > 0 && (
                                <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                  (1 admin + {subscription?.subscription.additionalUserSeats} additional)
                                </Typography>
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                    <Box className="text-center py-8">
                      <Typography variant="body1" color="textSecondary" className="mb-4">
                        {subscription?.subscription.status === 'canceled' ? 'Subscription Canceled' : 'No active subscription'}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={() => setCreateSubDialog(true)}
                      >
                        Create Subscription
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="textSecondary">
                        Plan
                      </Typography>
                      <Typography variant="body1" className="font-semibold">
                        Base Plan + {subscription?.subscription.additionalUserSeats} Additional Users
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="textSecondary">
                        Monthly Cost
                      </Typography>
                      <Typography variant="body1" className="font-semibold">
                        {formatAmount(subscription?.subscription.totalAmount || 0)}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="textSecondary">
                        User Seats
                      </Typography>
                      <Typography variant="body1" className="font-semibold">
                        {subscription?.subscription.usedUserSeats} / {subscription?.subscription.totalUserSeats}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {subscription?.remainingUserSeats} seats remaining
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="textSecondary">
                        Current Period
                      </Typography>
                      <Typography variant="body1" className="font-semibold">
                        {formatDate(subscription?.subscription.currentPeriodStart || null)} -{' '}
                        {formatDate(subscription?.subscription.currentPeriodEnd || null)}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="textSecondary">
                        Next Billing Date
                      </Typography>
                      <Typography variant="body1" className="font-semibold">
                        {formatDate(subscription?.subscription.currentPeriodEnd || null)}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="body2" color="textSecondary">
                        Last Synced
                      </Typography>
                      <Typography variant="body1" className="font-semibold">
                        {formatDate(subscription?.subscription.lastSyncedAt || null)}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Divider className="my-2" />
                      <Box className="flex gap-2 mt-4">
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => {
                            setAdditionalUsers(subscription?.subscription.additionalUserSeats || 0);
                            setUpdateSeatsDialog(true);
                          }}
                        >
                          Update Seats
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => setCancelDialog(true)}
                          disabled={subscription?.subscription.cancelAtPeriodEnd}
                        >
                          {subscription?.subscription.cancelAtPeriodEnd
                            ? 'Cancellation Pending'
                            : 'Cancel Subscription'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Method — only shown when an active subscription exists */}
          {subscription && subscription.subscription.status !== 'none' && subscription.subscription.status !== 'canceled' && (
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" className="font-semibold mb-4">
                  Payment Method
                </Typography>

                {subscription?.subscription.defaultPaymentMethod ? (
                  <Box>
                    <Box className="flex items-center mb-2">
                      <CreditCardIcon className="mr-2" />
                      <Typography variant="body1" className="font-semibold">
                        {subscription.subscription.defaultPaymentMethod.card.brand.toUpperCase()} ••••{' '}
                        {subscription.subscription.defaultPaymentMethod.card.last4}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Expires {subscription.subscription.defaultPaymentMethod.card.exp_month}/
                      {subscription.subscription.defaultPaymentMethod.card.exp_year}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No payment method on file
                  </Typography>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={updatingPaymentMethod ? <CircularProgress size={14} /> : <CreditCardIcon />}
                  onClick={handleUpdatePaymentMethod}
                  disabled={updatingPaymentMethod || !subscription}
                  sx={{ mt: 2 }}
                >
                  {updatingPaymentMethod ? 'Redirecting...' : 'Update Payment Method'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
          )}

          {/* Pending Changes */}
          {pendingChanges?.hasPending && pendingChanges.seatReduction && (
            <Grid size={{ xs: 12 }}>
              <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                <CardContent>
                  <Box className="flex justify-between items-center mb-3">
                    <Box className="flex items-center">
                      <WarningIcon color="warning" className="mr-2" />
                      <Typography variant="h6" className="font-semibold">
                        Pending Seat Reduction
                      </Typography>
                    </Box>
                    <Chip
                      label="Scheduled"
                      color="warning"
                      size="small"
                    />
                  </Box>

                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      Your seat count will be reduced from{' '}
                      <strong>{pendingChanges.seatReduction.currentAdditionalSeats + 1} seats</strong> to{' '}
                      <strong>{pendingChanges.seatReduction.newAdditionalSeats + 1} seats</strong> on{' '}
                      <strong>{formatDate(pendingChanges.seatReduction.scheduledFor)}</strong>.
                    </Typography>
                  </Alert>

                  {pendingChanges.seatReduction.usersToDeactivate?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" className="font-semibold mb-1">
                        Users to be deactivated:
                      </Typography>
                      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        {pendingChanges.seatReduction.usersToDeactivate.map((user: any, idx: number) => (
                          <li key={idx}>
                            <Typography variant="body2">
                              {user.email}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    </Box>
                  )}

                  <Box className="flex gap-2">
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<CancelIcon />}
                      onClick={async () => {
                        try {
                          setProcessing(true);
                          await cancelPendingReduction();
                          setSuccess('Pending seat reduction cancelled successfully');
                          await loadSubscriptionData();
                        } catch (err: any) {
                          setError(err.message || 'Failed to cancel pending reduction');
                        } finally {
                          setProcessing(false);
                        }
                      }}
                      disabled={processing}
                    >
                      Cancel Pending Reduction
                    </Button>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                      Users will remain active until {formatDate(pendingChanges.seatReduction.scheduledFor)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Billing History */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" className="font-semibold mb-4">
                  Billing History
                </Typography>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice ID</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="textSecondary">
                              No billing history available
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        invoices.map((invoice: any) => (
                          <TableRow key={invoice.invoiceId}>
                            <TableCell>
                              <Typography variant="body2" className="font-mono">
                                {invoice.invoiceId}
                              </Typography>
                            </TableCell>
                            <TableCell>{formatDate(invoice.paidAt)}</TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {invoice.description || (invoice.isProration ? 'Seat adjustment' : 'Monthly subscription')}
                              </Typography>
                              {invoice.isProration && (
                                <Chip
                                  label="Proration"
                                  size="small"
                                  variant="outlined"
                                  sx={{ mt: 0.5 }}
                                />
                              )}
                            </TableCell>
                            <TableCell>{formatAmount(invoice.amountPaid, invoice.currency)}</TableCell>
                            <TableCell>
                              <Chip
                                label={invoice.status.toUpperCase()}
                                size="small"
                                color={invoice.status === 'paid' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>
                              {invoice.receiptUrl && (
                                <IconButton
                                  size="small"
                                  href={invoice.receiptUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        )}

        {/* Create Subscription Dialog */}
        <Dialog
          open={createSubDialog}
          onClose={() => !processing && setCreateSubDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { minHeight: '400px' } }}
        >
          <DialogTitle>Create Subscription</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Alert severity="info" sx={{ mb: 4 }}>
              <Typography variant="body2">
                <strong>Base Plan:</strong> 1 admin user for $250/month<br />
                <strong>Additional Users:</strong> $50/month per sales rep
              </Typography>
            </Alert>

            <Box sx={{ mb: 4 }}>
              <TextField
                fullWidth
                label="Number of Additional Sales Rep Users"
                type="number"
                value={additionalUsers ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setAdditionalUsers(0);
                  } else {
                    const numValue = parseInt(value, 10);
                    if (!isNaN(numValue) && numValue >= 0) {
                      setAdditionalUsers(numValue);
                    }
                  }
                }}
                InputProps={{ inputProps: { min: 0 } }}
                helperText="Enter the number of sales rep users you need (admin seat is already included)"
                sx={{ mb: 2 }}
              />
            </Box>

            <Box className="bg-gray-100 p-4 rounded" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" className="font-semibold mb-2">
                Pricing Summary
              </Typography>
              <Box className="space-y-1">
                <Box className="flex justify-between">
                  <Typography variant="body2" color="textSecondary">
                    Base Plan (1 Admin)
                  </Typography>
                  <Typography variant="body2">
                    {formatAmount(25000)}
                  </Typography>
                </Box>
                {additionalUsers > 0 && (
                  <Box className="flex justify-between">
                    <Typography variant="body2" color="textSecondary">
                      {additionalUsers} Sales Rep {additionalUsers === 1 ? 'Seat' : 'Seats'}
                    </Typography>
                    <Typography variant="body2">
                      {formatAmount(additionalUsers * 5000)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box className="flex justify-between">
                  <Typography variant="body1" className="font-bold">
                    Total Monthly Cost
                  </Typography>
                  <Typography variant="h6" className="font-bold" color="primary">
                    {formatAmount(25000 + additionalUsers * 5000)}
                  </Typography>
                </Box>
                <Box className="flex justify-between mt-2">
                  <Typography variant="caption" color="textSecondary">
                    Total Seats:
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {1 + additionalUsers} {(1 + additionalUsers) === 1 ? 'user' : 'users'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={() => {
                setCreateSubDialog(false);
                setAdditionalUsers(0);
              }}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSubscription}
              variant="contained"
              color="primary"
              disabled={processing}
              startIcon={processing ? <CircularProgress size={20} /> : null}
            >
              {processing ? 'Processing...' : 'Proceed to Checkout'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Update Seats Dialog */}
        <Dialog
          open={updateSeatsDialog}
          onClose={() => !processing && setUpdateSeatsDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { minHeight: '450px' } }}
        >
          <DialogTitle>Update User Seats</DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  You currently have <strong>{subscription?.subscription.additionalUserSeats || 0}</strong> additional sales rep {(subscription?.subscription.additionalUserSeats || 0) === 1 ? 'seat' : 'seats'}
                  {(subscription?.subscription.additionalUserSeats ?? 0) > 0 && ` (${subscription?.subscription.usedUserSeats || 0} used, ${subscription?.remainingUserSeats || 0} available)`}
                </Typography>
              </Alert>
            </Box>

            <Box sx={{ mb: 4 }}>
              <TextField
                fullWidth
                label="Number of Additional Sales Rep Seats"
                type="number"
                value={additionalUsers ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setAdditionalUsers(0);
                  } else {
                    const numValue = parseInt(value, 10);
                    if (!isNaN(numValue) && numValue >= 0) {
                      setAdditionalUsers(numValue);
                    }
                  }
                }}
                InputProps={{ inputProps: { min: 0 } }}
                helperText="Enter the total number of additional sales rep seats you need (not including the base admin seat)"
                sx={{ mb: 2 }}
              />

              {additionalUsers !== (subscription?.subscription.additionalUserSeats || 0) && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  Change: {subscription?.subscription.additionalUserSeats || 0} → {additionalUsers} seats
                  ({additionalUsers > (subscription?.subscription.additionalUserSeats || 0) ? '+' : ''}{additionalUsers - (subscription?.subscription.additionalUserSeats || 0)})
                </Typography>
              )}
            </Box>

            <Box className="bg-gray-100 p-4 rounded" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" className="font-semibold mb-2">
                Pricing Breakdown
              </Typography>
              <Box className="space-y-1">
                <Box className="flex justify-between">
                  <Typography variant="body2" color="textSecondary">
                    Base Plan (1 Admin)
                  </Typography>
                  <Typography variant="body2">
                    {formatAmount(25000)}
                  </Typography>
                </Box>
                {additionalUsers > 0 && (
                  <Box className="flex justify-between">
                    <Typography variant="body2" color="textSecondary">
                      {additionalUsers} Additional {additionalUsers === 1 ? 'Seat' : 'Seats'}
                    </Typography>
                    <Typography variant="body2">
                      {formatAmount(additionalUsers * 5000)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box className="flex justify-between">
                  <Typography variant="body1" className="font-bold">
                    New Monthly Total
                  </Typography>
                  <Typography variant="h6" className="font-bold" color="primary">
                    {formatAmount(25000 + additionalUsers * 5000)}
                  </Typography>
                </Box>
              </Box>

              {additionalUsers !== (subscription?.subscription.additionalUserSeats || 0) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    {additionalUsers > (subscription?.subscription.additionalUserSeats || 0)
                      ? 'The prorated amount will be charged immediately to your payment method on file.'
                      : `Seat reduction will be scheduled for the end of your billing period (${formatDate(subscription?.subscription.currentPeriodEnd || null)}). Selected users will retain full access until then.`}
                  </Typography>
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={() => {
                setUpdateSeatsDialog(false);
                setAdditionalUsers(subscription?.subscription.additionalUserSeats || 0);
              }}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateSeatsConfirm}
              variant="contained"
              color="primary"
              disabled={processing || additionalUsers === (subscription?.subscription.additionalUserSeats || 0)}
            >
              {processing ? <CircularProgress size={24} /> : 'Review Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Update Seats Confirmation Dialog */}
        <Dialog
          open={updateSeatsConfirmDialog}
          onClose={() => !processing && setUpdateSeatsConfirmDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box className="flex items-center">
              <WarningIcon color="warning" sx={{ mr: 2 }} />
              Confirm Seat Update
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Please confirm your subscription changes:
            </Typography>

            <Box className="bg-blue-50 p-4 rounded" sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" className="font-semibold mb-2">
                    Seat Changes
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">
                    Current Additional Seats:
                  </Typography>
                  <Typography variant="body1" className="font-semibold">
                    {subscription?.subscription.additionalUserSeats || 0}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">
                    New Additional Seats:
                  </Typography>
                  <Typography variant="body1" className="font-semibold" color="primary">
                    {additionalUsers}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 2 }} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">
                    Current Monthly Cost:
                  </Typography>
                  <Typography variant="body1" className="font-semibold">
                    {formatAmount(25000 + (subscription?.subscription.additionalUserSeats || 0) * 5000)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">
                    New Monthly Cost:
                  </Typography>
                  <Typography variant="body1" className="font-semibold" color="primary">
                    {formatAmount(25000 + additionalUsers * 5000)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {selectedUsersToDeactivate.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                <AlertTitle>Users to be Deactivated</AlertTitle>
                <Typography variant="body2">
                  The following users will be deactivated at the end of your billing period ({formatDate(subscription?.subscription.currentPeriodEnd || null)}):
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  {selectedUsersToDeactivate.map(userId => {
                    const user = activeUsers.find(u => (u._id || u.id) === userId);
                    return user ? (
                      <li key={userId}>
                        {user.name} ({user.email})
                      </li>
                    ) : null;
                  })}
                </ul>
              </Alert>
            )}

            {additionalUsers > (subscription?.subscription.additionalUserSeats || 0) ? (
              <Alert severity="warning">
                <AlertTitle>Immediate Payment Required</AlertTitle>
                <Typography variant="body2">
                  You are <strong>adding {additionalUsers - (subscription?.subscription.additionalUserSeats || 0)} seat(s)</strong>.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Important:</strong> The prorated amount will be charged immediately to your payment method on file.
                  Your next regular invoice will show the full new monthly amount of {formatAmount(25000 + additionalUsers * 5000)}.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info">
                <AlertTitle>Scheduled for End of Billing Period</AlertTitle>
                <Typography variant="body2">
                  You are <strong>removing {(subscription?.subscription.additionalUserSeats || 0) - additionalUsers} seat(s)</strong>.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Important:</strong> The seat reduction will take effect on {formatDate(subscription?.subscription.currentPeriodEnd || null)}.
                  Selected users will remain active until then, and you will continue paying for these seats through the end of your current billing period.
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={() => setUpdateSeatsConfirmDialog(false)}
              disabled={processing}
              variant="outlined"
            >
              Back to Edit
            </Button>
            <Button
              onClick={handleUpdateSeats}
              variant="contained"
              color="primary"
              disabled={processing}
              startIcon={processing ? <CircularProgress size={20} /> : null}
            >
              {processing ? 'Updating...' : 'Confirm Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Selection Dialog for Seat Reduction */}
        <Dialog
          open={userSelectionDialog}
          onClose={() => !processing && setUserSelectionDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Select Users to Deactivate</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Seat Reduction Requires User Deactivation</AlertTitle>
              You are reducing your seats from {subscription?.subscription.totalUserSeats || 0} to {1 + additionalUsers}.
              Please select {requiredDeactivations} user(s) to deactivate.
            </Alert>

            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Selected users will retain full access until the end of your current billing period</strong> ({formatDate(subscription?.subscription.currentPeriodEnd || null)}).
              After this date, they will be automatically deactivated. You can reactivate them later if you purchase additional seats.
            </Typography>

            <List>
              {activeUsers
                .filter((user: any) => user.role !== 'admin') // Don't allow deactivating admins
                .map((user: any) => (
                  <ListItem key={user._id || user.id}>
                    <ListItemIcon>
                      <Checkbox
                        checked={selectedUsersToDeactivate.includes(user._id || user.id)}
                        onChange={() => handleUserSelectionToggle(user._id || user.id)}
                        disabled={
                          selectedUsersToDeactivate.length >= requiredDeactivations &&
                          !selectedUsersToDeactivate.includes(user._id || user.id)
                        }
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={user.name}
                      secondary={user.email}
                    />
                  </ListItem>
                ))}
            </List>

            {selectedUsersToDeactivate.length !== requiredDeactivations && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Please select exactly {requiredDeactivations} user(s). Currently selected: {selectedUsersToDeactivate.length}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setUserSelectionDialog(false);
                setSelectedUsersToDeactivate([]);
              }}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleProceedWithUserSelection}
              variant="contained"
              color="primary"
              disabled={selectedUsersToDeactivate.length !== requiredDeactivations || processing}
            >
              Continue with Deactivation
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Subscription Dialog */}
        <Dialog open={cancelDialog} onClose={() => !processing && setCancelDialog(false)}>
          <DialogTitle>Cancel Subscription</DialogTitle>
          <DialogContent>
            <DialogContentText className="mb-4">
              Are you sure you want to cancel your subscription?
            </DialogContentText>
            <TextField
              fullWidth
              label="Reason for cancellation (optional)"
              multiline
              rows={3}
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              className="mb-4"
            />
            <Alert severity="info">
              Your subscription will remain active until {formatDate(subscription?.subscription.currentPeriodEnd || null)}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialog(false)} disabled={processing}>
              Keep Subscription
            </Button>
            <Button
              onClick={handleCancelSubscription}
              variant="contained"
              color="error"
              disabled={processing}
            >
              {processing ? <CircularProgress size={24} /> : 'Confirm Cancellation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Checkout Success Dialog */}
        <Dialog
          open={successDialog}
          onClose={() => setSuccessDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle className="text-center">
            <Box className="flex flex-col items-center">
              <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h5" className="font-bold">
                Welcome to Lushin!
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box className="text-center mb-4">
              <Typography variant="body1" color="textSecondary" className="mb-4">
                Your subscription has been successfully activated. Thank you for choosing Lushin to
                supercharge your sales team!
              </Typography>
              <Divider className="my-4" />
            </Box>

            {checkoutDetails && (
              <Box>
                <Typography variant="h6" className="font-semibold mb-3 text-center">
                  Subscription Details
                </Typography>

                <Box className="bg-gray-50 p-4 rounded-lg mb-3">
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <Box className="flex justify-between items-center mb-2">
                        <Typography variant="body2" color="textSecondary">
                          Monthly Subscription
                        </Typography>
                        <Typography variant="h6" className="font-bold" color="primary">
                          {formatAmount(checkoutDetails.amountPaid)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Divider />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Box className="bg-blue-50 p-3 rounded">
                        <Typography variant="subtitle2" className="font-bold mb-2" color="primary">
                          Seat Allocation Breakdown
                        </Typography>
                        <Box className="space-y-1">
                          <Box className="flex justify-between items-center">
                            <Typography variant="body2" color="textSecondary">
                              Base Plan (Admin Seat)
                            </Typography>
                            <Typography variant="body2" className="font-semibold">
                              1 seat
                            </Typography>
                          </Box>
                          {checkoutDetails.additionalSeats > 0 && (
                            <Box className="flex justify-between items-center">
                              <Typography variant="body2" color="textSecondary">
                                Additional Sales Rep Seats
                              </Typography>
                              <Typography variant="body2" className="font-semibold">
                                {checkoutDetails.additionalSeats} {checkoutDetails.additionalSeats === 1 ? 'seat' : 'seats'}
                              </Typography>
                            </Box>
                          )}
                          <Divider sx={{ my: 1 }} />
                          <Box className="flex justify-between items-center">
                            <Typography variant="body2" className="font-bold">
                              Total Seats Available
                            </Typography>
                            <Typography variant="body1" className="font-bold" color="primary">
                              {checkoutDetails.totalSeats} {checkoutDetails.totalSeats === 1 ? 'seat' : 'seats'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Box className="flex justify-between items-center mb-1">
                        <Typography variant="body2" color="textSecondary">
                          Admin Account
                        </Typography>
                        <Typography variant="body2" className="font-semibold">
                          {auth.currentUser?.email}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {checkoutDetails.additionalSeats > 0 && (
                  <Alert severity="warning" className="mb-3">
                    <Typography variant="body2" className="font-semibold mb-1">
                      Action Required: Add Sales Rep Users
                    </Typography>
                    <Typography variant="body2">
                      You have purchased {checkoutDetails.additionalSeats} additional {checkoutDetails.additionalSeats === 1 ? 'seat' : 'seats'} for sales representatives.
                      Please go to the Users tab to create accounts for your sales team members.
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => navigate('/users')}
                      sx={{ mt: 1 }}
                    >
                      Go to Users Tab
                    </Button>
                  </Alert>
                )}

                <Alert severity="success" className="mb-3">
                  <Typography variant="body2">
                    ✓ Admin access has been granted to: <strong>{auth.currentUser?.email}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Your account has been activated and you can now access all features of Lushin.
                  </Typography>
                </Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions className="justify-center pb-4">
            <Button
              onClick={() => setSuccessDialog(false)}
              variant="contained"
              color="primary"
              size="large"
            >
              Get Started
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default Subscription;
